
import { useState, useEffect, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

export function useAuthState() {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // A ref, not state: as a dependency it re-ran this effect and opened a second
  // auth subscription plus a duplicate getSession() on every load.
  const initializedRef = useRef(false);

  useEffect(() => {
    let isMounted = true;
    let sessionInitialized = false;

    // Set up the auth state listener first - this handles session changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        if (!isMounted) return;

        console.log('[Auth] Auth state changed:', event, currentSession?.user?.email);

        // Handle token refresh errors
        if (event === 'SIGNED_OUT' && initializedRef.current && !sessionInitialized) {
          console.warn('[Auth] Unexpected sign out event (possible refresh token error)');
        }

        if (currentSession?.user) {
          setSession(currentSession);
          setUser(currentSession.user);
          sessionInitialized = true;
        } else if (event === 'SIGNED_OUT') {
          setSession(null);
          setUser(null);
          sessionInitialized = true;
        }

        // Mark as no longer loading after first subscription event
        if (sessionInitialized) {
          setIsLoading(false);
        }

        // Redirect after initialized if session is lost
        if (event === 'SIGNED_OUT' && initializedRef.current) {
          console.log('[Auth] Session lost after initialization, redirecting to login');
          window.location.href = '/auth';
        }
      }
    );

    // Explicitly restore session from localStorage to ensure it's available
    const restoreSession = async () => {
      try {
        const { data: { session: currentSession }, error } = await supabase.auth.getSession();

        if (!isMounted) return;

        if (error) {
          console.warn('[Auth] Error fetching session:', error.message);

          // Handle refresh token errors
          if (error.message?.includes('Refresh Token') || error.message?.includes('refresh_token')) {
            console.error('[Auth] Refresh token error - clearing invalid session');
            // Clear the invalid token from localStorage
            try {
              await supabase.auth.signOut();
            } catch (e) {
              // Ignore errors during cleanup
              console.debug('[Auth] Cleanup error during refresh token recovery:', e);
            }
            setSession(null);
            setUser(null);
            sessionInitialized = true;
          } else if (error.status === 401 || error.status === 400) {
            // Auth error - session is invalid
            setSession(null);
            setUser(null);
            sessionInitialized = true;
          }
        } else if (currentSession?.user) {
          console.log('[Auth] Session restored:', currentSession.user.email);
          setSession(currentSession);
          setUser(currentSession.user);
          sessionInitialized = true;
        } else {
          console.log('[Auth] No session in localStorage');
          setSession(null);
          setUser(null);
          sessionInitialized = true;
        }
      } catch (err) {
        console.error('[Auth] Error restoring session:', err);
        if (isMounted) {
          setSession(null);
          setUser(null);
        }
      } finally {
        if (isMounted && sessionInitialized) {
          setIsLoading(false);
          initializedRef.current = true;
        }
      }
    };

    // Restore session (subscription will handle real-time changes)
    restoreSession();

    // Clean up
    return () => {
      isMounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  return { user, session, isLoading, isAuthenticated: !!user };
}
